import {
  BlockStack,
  Box,
  Card,
  Layout,
  Page,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonPage,
  SkeletonTabs,
} from "@shopify/polaris";

export function AdminPageSkeleton({ title = true }) {
  return (
    <SkeletonPage primaryAction>
      {title && <SkeletonDisplayText size="large" />}
      <Layout>
        <Layout.Section>
          <Card>
            <SkeletonBodyText lines={6} />
          </Card>
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  );
}

export function DashboardSkeleton() {
  return (
    <SkeletonPage primaryAction>
      {/* Greeting header skeleton */}
      <Box paddingBlockEnd="500">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <SkeletonDisplayText size="large" />
            <Box paddingBlockStart="200">
              <SkeletonBodyText lines={1} />
            </Box>
          </div>
          <div style={{ width: 120, flexShrink: 0 }}>
            <SkeletonBodyText lines={1} />
          </div>
        </div>
      </Box>

      {/* Detail metrics grid (4 cards) */}
      <Box paddingBlockEnd="500">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <Card key={`metric-${i}`}>
              <Box padding="400">
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ width: 40, height: 40, background: "#f0f0f0", borderRadius: 4, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <SkeletonBodyText lines={1} />
                    <Box paddingBlockStart="200">
                      <SkeletonDisplayText size="small" />
                    </Box>
                  </div>
                </div>
              </Box>
            </Card>
          ))}
        </div>
      </Box>

      {/* Minutes usage card */}
      <Box paddingBlockEnd="500">
        <Card>
          <Box padding="400">
            <SkeletonDisplayText size="medium" />
            <Box paddingBlockStart="400">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 24,
                }}
              >
                {[1, 2, 3, 4].map((i) => (
                  <div key={`minutes-${i}`}>
                    <SkeletonBodyText lines={1} />
                    <Box paddingBlockStart="200">
                      <SkeletonDisplayText size="small" />
                    </Box>
                  </div>
                ))}
              </div>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Status rows */}
      <Box paddingBlockEnd="500">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {[1, 2].map((i) => (
            <div
              key={`status-${i}`}
              style={{
                padding: 16,
                border: "1px solid #e1e3e5",
                borderRadius: 6,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <SkeletonBodyText lines={1} />
              <div style={{ width: 100 }}>
                <SkeletonBodyText lines={1} />
              </div>
            </div>
          ))}
        </div>
      </Box>

      {/* Overview section */}
      <Box paddingBlockEnd="500">
        <Box paddingBlockEnd="300">
          <SkeletonDisplayText size="medium" />
        </Box>
        <Card>
          <Box padding="400">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 24,
              }}
            >
              {[1, 2, 3, 4].map((i) => (
                <div key={`overview-${i}`}>
                  <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 40, height: 40, background: "#f0f0f0", borderRadius: 4, flexShrink: 0 }} />
                    <SkeletonBodyText lines={1} />
                  </div>
                  <SkeletonDisplayText size="small" />
                  <Box paddingBlockStart="200">
                    <SkeletonBodyText lines={1} />
                  </Box>
                </div>
              ))}
            </div>
          </Box>
        </Card>
      </Box>

      {/* Recent activity and help sections */}
      <Box paddingBlockEnd="500">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 16,
          }}
        >
          {[1, 2].map((i) => (
            <Card key={`section-${i}`}>
              <Box padding="400">
                <SkeletonDisplayText size="medium" />
                <Box paddingBlockStart="300">
                  <SkeletonBodyText lines={4} />
                </Box>
              </Box>
            </Card>
          ))}
        </div>
      </Box>

      {/* Quick actions */}
      <Box paddingBlockEnd="500">
        <Card>
          <Box padding="400">
            <SkeletonDisplayText size="medium" />
            <Box paddingBlockStart="300">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 16,
                }}
              >
                {[1, 2, 3, 4].map((i) => (
                  <div key={`action-${i}`} style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <div style={{ width: 40, height: 40, background: "#f0f0f0", borderRadius: 4, flexShrink: 0 }} />
                    <SkeletonBodyText lines={2} />
                  </div>
                ))}
              </div>
            </Box>
          </Box>
        </Card>
      </Box>
    </SkeletonPage>
  );
}

export function TablePageSkeleton() {
  return (
    <SkeletonPage primaryAction>
      <SkeletonDisplayText size="large" />
      <Box paddingBlockStart="400">
        <Card>
          <SkeletonTabs count={3} />
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        </Card>
      </Box>
    </SkeletonPage>
  );
}

export function StorefrontSkeleton() {
  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <BlockStack gap="400">
        <SkeletonDisplayText size="large" />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 16,
          }}
        >
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <Box padding="400">
                <SkeletonDisplayText size="small" />
                <Box paddingBlockStart="300">
                  <SkeletonBodyText lines={3} />
                </Box>
              </Box>
            </Card>
          ))}
        </div>
      </BlockStack>
    </div>
  );
}

export function ConsultantDashboardSkeleton() {
  return (
    <div style={{ display: "flex", minHeight: "60vh" }}>
      <div
        style={{
          width: 240,
          padding: 16,
          borderRight: "1px solid #e1e3e5",
          flexShrink: 0,
        }}
      >
        <SkeletonDisplayText size="small" />
        <Box paddingBlockStart="400">
          <SkeletonBodyText lines={6} />
        </Box>
      </div>
      <div style={{ flex: 1, padding: 24 }}>
        <SkeletonDisplayText size="large" />
        <Box paddingBlockStart="400">
          <SkeletonBodyText lines={8} />
        </Box>
      </div>
    </div>
  );
}

export function RouteLoadingFallback() {
  return (
    <div className="route-loading-shell">
      <AdminPageSkeleton />
    </div>
  );
}
